/**
 * Composant formulaire d'inscription pour le Back-Office
 * Affiche les informations de la demande d'inscription en mode éditable
 * Layout en deux colonnes avec formulaire modifiable
 */

import React, { useState, useEffect } from "react";
import { IInscription } from "../../../../../models/interfaces/IInscription";
import { useFormContext, FormState } from "react-hook-form";
import { toast } from "react-toastify";

import InscriptionFormSociete from "./InscriptionFormSociete";
import InscriptionFormDemandeur from "./InscriptionFormDemandeur";
import InscriptionFormComplementaires from "./InscriptionFormComplementaires";
import InscriptionFormConditions from "./InscriptionFormConditions";
import InscriptionFormMarques from "./InscriptionFormMarques";
import { ETATS_DEMANDE } from "../../../../../config/helpers";

/**
 * Props du composant InscriptionFormContentBO
 */
interface InscriptionFormContentBOProps {
  /** Données de la demande d'inscription */
  data: IInscription;
  /** Liste des pays pour le dropdown */
  pays?: any[];
  /** Liste des secteurs d'activité pour le dropdown */
  secteurActivites?: any[];
  /** Liste des fonctions pour le dropdown */
  fonctions?: any[];
  /** Mode lecture seule si la demande n'est pas prise en charge */
  readOnly?: boolean;
  /** Masquer les boutons si l'utilisateur n'est pas le propriétaire */
  hideButtons?: boolean;
  /** Handler pour sauvegarder et continuer */
  onSaveAndContinue?: (formData: IInscription, typeCompte?: number, shouldValidate?: boolean) => Promise<boolean>;
  /** Handler pour enregistrer les modifications sans valider */
  onSave?: (formData: IInscription) => Promise<boolean>;
  /** Handler pour la validation du compte */
  onValider?: (typeCompte?: number) => void;
  /** Handler pour le refus du compte */
  onRefuser?: () => void;
  /** Handler pour désactiver le compte */
  onDesactiver?: () => void;
  /** Handler pour l'annulation */
  onAnnuler?: () => void;
  /** Indique si une mise à jour est en cours */
  isUpdating?: boolean;
  /** Indique si une sauvegarde simple est en cours */
  isSaving?: boolean;
  /** Erreur API du SIREN */
  sirenApiError?: string | null;
  /** Erreur API de l'email */
  emailApiError?: string | null;
  /** Fonction pour vérifier l'email manuellement */
  checkEmail?: (emailToCheck?: string) => Promise<boolean>;
  /** Indique si une vérification email est en cours */
  isCheckingEmail?: boolean;
  /** État du formulaire pour validation */
  formState?: FormState<IInscription>;
  /** Indique si le pays est France ou assimilé */
  isFranceOrAssimile?: boolean;
}

/**
 * Composant d'affichage et d'édition d'une demande d'inscription en BO
 * Utilise React Hook Form via FormProvider défini dans le composant parent
 */
const InscriptionFormContentBO: React.FC<InscriptionFormContentBOProps> = ({
  data,
  pays = [],
  secteurActivites = [],
  fonctions = [],
  readOnly = false,
  hideButtons = false,
  onSaveAndContinue,
  onSave,
  onValider,
  onRefuser,
  onDesactiver,
  onAnnuler,
  isUpdating = false,
  isSaving = false,
  sirenApiError = null,
  emailApiError = null,
  checkEmail,
  isCheckingEmail = false,
  formState,
  isFranceOrAssimile = false,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isMarquesOpen, setIsMarquesOpen] = useState(false);
  const [showMarquesBlock, setShowMarquesBlock] = useState(false);
  
  const { watch, getValues, trigger } = useFormContext<IInscription>();
  const typeCompte = watch("IdTypeDeCompteSociete");
  
  // Vérifier si la demande est validée (état 2 = VALIDE)
  const isDemandeValidee = data.IdEtatDemandeInscription === ETATS_DEMANDE.VALIDE;
  
  // Vérifier si la demande est refusée (état 5 = REFUSE)
  const isDemandeRefusee = data.IdEtatDemandeInscription === ETATS_DEMANDE.REFUSE;
  
  // Détecter si l'état est AFFECTATION_MARQUES ou VALIDE pour afficher le bloc automatiquement
  useEffect(() => {
    if (data.IdEtatDemandeInscription === ETATS_DEMANDE.AFFECTATION_MARQUES || 
        data.IdEtatDemandeInscription === ETATS_DEMANDE.VALIDE) {
      setShowMarquesBlock(true);
      setIsMarquesOpen(true);
      // Laisser le premier bloc ouvert
    }
  }, [data.IdEtatDemandeInscription]);
  
  // Conditions centralisées
  const isFieldDisabled = readOnly || showMarquesBlock || isDemandeValidee || isDemandeRefusee;
  const showButtons = !showMarquesBlock && !isDemandeValidee && !isDemandeRefusee && !hideButtons;
  const isHorsPerimetre = Number(typeCompte) === 4;

  const handleValiderClick = async () => {
    // Déclencher la validation de tous les champs obligatoires
    const isValid = await trigger();
    
    // Si la validation échoue, arrêter ici (les champs en erreur s'afficheront en rouge)
    if (!isValid) {
      toast.error("Veuillez remplir tous les champs obligatoires avant de valider");
      return;
    }
    
    // Vérifier s'il y a une erreur API sur le SIREN (SIREN déjà en base, non diffusible, etc.)
    if (sirenApiError) {
      toast.error(sirenApiError);
      return;
    }
    
    // Vérifier l'email avant de continuer
    if (checkEmail) {
      const emailIsValid = await checkEmail();
      if (!emailIsValid) {
        toast.error("Veuillez corriger l'email avant de valider");
        return;
      }
    }
    
    // Vérifier s'il y a une erreur API sur l'email (email déjà utilisé)
    if (emailApiError) {
      toast.error(emailApiError);
      return;
    }
    
    // Récupérer toutes les données du formulaire
    const formData = getValues();
    
    // Déterminer l'état selon le type de compte
    // Si Hors périmètre (4) → VALIDE, sinon → AFFECTATION_MARQUES
    const shouldValidate = isHorsPerimetre;
    
    // Appeler l'API pour sauvegarder dans tous les cas
    const saved = await onSaveAndContinue?.(formData, Number(typeCompte), shouldValidate);
    
    if (saved) {
      // Après sauvegarde, vérifier le type de compte
      if (!isHorsPerimetre) {
        // Si != 4, afficher le bloc d'affectation des marques
        setShowMarquesBlock(true);
        setIsMarquesOpen(true);
      } 
    }
  };

  return (
    <>
      <div className="card mb-3 shadow-sm">
        <div 
          className="card-header d-flex justify-content-between align-items-center" 
          style={{ 
            cursor: "pointer",
            background: "linear-gradient(135deg, #4a6fa5 0%, #5681bb 100%)",
            color: "white",
            padding: "1rem 1.5rem",
            borderRadius: "0.375rem 0.375rem 0 0"
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <h5 className="mb-0 fw-semibold">
            <i className="bi bi-file-text me-2"></i>
            Informations du formulaire d&apos;inscription
          </h5>
          <i className={`bi bi-chevron-${isOpen ? "up" : "down"} fs-5`}></i>
        </div>
        <div style={{ display: isOpen ? "block" : "none" }}>
          <div className="card-body">
            <div className="row mb-3">
              <InscriptionFormSociete pays={pays} disabled={isFieldDisabled} isFranceOrAssimile={isFranceOrAssimile} />
              <InscriptionFormDemandeur fonctions={fonctions} disabled={isFieldDisabled} />
            </div>

            <InscriptionFormComplementaires 
              data={data}
              secteurActivites={secteurActivites}
              disabled={isFieldDisabled}
            />

            <InscriptionFormConditions
              data={data}
              disabled={isFieldDisabled}
              showButtons={showButtons}
              isUpdating={isUpdating}
              isSaving={isSaving}
              readOnly={readOnly}
              onAnnuler={onAnnuler}
              onSave={onSave}
              onValider={handleValiderClick}
              onRefuser={onRefuser}
              sirenApiError={sirenApiError}
              emailApiError={emailApiError}
              checkEmail={checkEmail}
              isCheckingEmail={isCheckingEmail}
              formState={formState}
            />
          </div>
        </div>
      </div>

      {(showMarquesBlock || isDemandeValidee) && (
        <InscriptionFormMarques
          isOpen={isMarquesOpen}
          isUpdating={isUpdating}
          typeCompte={typeCompte}
          IdSociete={data.IdSociete}
          IdEtatDemandeInscription={data.IdEtatDemandeInscription}
          onToggle={() => setIsMarquesOpen(!isMarquesOpen)}
          onAnnuler={onAnnuler}
          onValider={onValider}
          onRefuser={onRefuser}
          onDesactiver={onDesactiver}
        />
      )}
    </>
  );
};

export default InscriptionFormContentBO;
